import * as React from 'react'

import { ChevronLeftIcon, XIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { Mention, MentionsInput } from 'react-mentions'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { ITag } from '~/client/src/shared/models/Tag'
import { NOOP } from '~/client/src/shared/utils/noop'

import BaseAssignmentsStore from '../../stores/BaseAssignments.store'
import BaseFollowingsStore from '../../stores/BaseFollowings.store'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import TagsStore from '../../stores/domain/Tags.store'
import UserProjectsStore from '../../stores/domain/UserProjects.store'
import InitialState from '../../stores/InitialState'
import { getElementHeightByRef } from '../../utils/RefHelpers'
import Dimmer from '../Dimmer'
import UsersDirectory from '../UsersDirectory/UsersDirectory'
import ActionBarInputWithMentionsStore, {
  ICustomMention,
  IMention,
  MENTION_CHAR,
} from './ActionBarInputWithMentions.store'
import MentionRow from './components/MentionRow'

import './ActionBarInputWithMentions.scss'

const DEFAULT_BOTTOM_VALUE = 97
const BOTTOM_OFFSET = 10

interface IActionBarInput {
  value: string
  plainText: string
  mentions: ITag[]
  onChange: (value: string, text?: string, mentions?: ITag[]) => void
  onSuggestionsPopupShow?: () => void
  onSuggestionsPopupHide?: () => void
  placeholder?: string
  className?: string
  entityId: string
  assignmentsStore: BaseAssignmentsStore
  followingsStore: BaseFollowingsStore
  parentRef?: HTMLDivElement

  state?: InitialState
  tagsStore?: TagsStore
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
  isFocused?: boolean
}

const iconSize = 20

@inject('state', 'tagsStore', 'projectMembersStore', 'userProjectsStore')
@observer
export default class ActionBarInputWithMentions extends React.Component<IActionBarInput> {
  public static defaultProps = {
    className: 'mention-input mb10 no-resize full-width',
    onSuggestionsPopupShow: NOOP,
    onSuggestionsPopupHide: NOOP,
  }

  private readonly store: ActionBarInputWithMentionsStore = null
  private textarea: HTMLInputElement
  private suggestionsPortal: HTMLDivElement

  public constructor(props: IActionBarInput) {
    super(props)

    this.store = new ActionBarInputWithMentionsStore(
      props.state,
      props.assignmentsStore,
      props.followingsStore,
      props.projectMembersStore,
      props.userProjectsStore,
      props.entityId,
      props.tagsStore,
      props.onSuggestionsPopupShow,
      props.onSuggestionsPopupHide,
      props.onChange,
      props.value,
      props.plainText,
      props.mentions,
    )
  }

  public componentDidUpdate(prevProps: Readonly<IActionBarInput>) {
    const { value, plainText, mentions } = this.props

    if (
      value !== this.store.currentValue ||
      plainText !== this.store.currentPlainText
    ) {
      this.store.setValues(value, plainText, mentions)
    }

    const shouldTriggerMentionMenu =
      this.textarea &&
      value !== prevProps.value &&
      value[value.length - 1] === MENTION_CHAR

    if (shouldTriggerMentionMenu) {
      this.textarea.focus()

      // hack for mobile devices to set cursor at the end of focused input
      const plainValue = this.textarea.value
      this.textarea.value = ''
      this.textarea.value = plainValue
    }

    if (!value && this.textarea) {
      this.textarea.style.height = 'auto'
    }
  }

  public render() {
    const { value, placeholder, className } = this.props
    const { getSuggestions } = this.store

    return (
      <>
        {this.renderEmbeddedUserDirectory()}
        {this.renderSuggestionsPopup()}
        <div
          className="mention-input-container tw:[&_textarea]:outline-none"
          onMouseDown={e => e.stopPropagation()}
        >
          <MentionsInput
            inputRef={this.setTextareaRef}
            value={value}
            onChange={this.handleChange}
            className={className}
            placeholder={placeholder}
            suggestionsPortalHost={this.suggestionsPortal}
          >
            <Mention
              trigger={MENTION_CHAR}
              data={getSuggestions}
              renderSuggestion={this.renderOptionRow}
              className="mention-input__mention"
              appendSpaceOnAdd={true}
            />
          </MentionsInput>
        </div>
      </>
    )
  }

  private setTextareaRef = ref => {
    this.textarea = ref
  }

  private setSuggestionsPortalRef = ref => {
    this.suggestionsPortal = ref
  }

  private renderOptionRow = (entry: ICustomMention): JSX.Element => {
    const { showSuggestionsPopup, hideSuggestionsPopup } = this.store

    return (
      <MentionRow
        entry={entry}
        onMount={showSuggestionsPopup}
        onUnmount={hideSuggestionsPopup}
      />
    )
  }

  private renderSuggestionsPopup() {
    const { isSuggestionsPopupOpen, showUserDirectory } = this.store
    const bottomString = `${this.bottomValue}px`
    return (
      <>
        {isSuggestionsPopupOpen && (
          <Dimmer
            shown={true}
            onClick={NOOP}
            style={{ bottom: bottomString }}
          />
        )}
        <div
          className={classList({
            'custom-suggestions-popup': true,
            // The wrapper stays mounted while closed, so the sheet chrome is
            // scoped to the open state. Shadow is the panel-edge convention
            // rotated for a surface that rises out of the composer.
            'tw:rounded-t-lg tw:border tw:border-b-0 tw:border-solid tw:border-border tw:shadow-[0_-4px_16px_rgba(0,0,0,0.1)]':
              isSuggestionsPopupOpen,
          })}
          style={{ bottom: bottomString }}
        >
          {isSuggestionsPopupOpen && (
            <div className="row py12 relative">
              <XIcon size={16} className="absolute close-handler" />
              <div className="text large bold center">
                {Localization.translator.contacts}
              </div>
              <span
                className="absolute text large blue-highlight action-handler pointer"
                onMouseDown={showUserDirectory}
              >
                {Localization.translator.gotToDirectory}
              </span>
            </div>
          )}
          <div ref={this.setSuggestionsPortalRef} />
        </div>
      </>
    )
  }

  private get bottomValue() {
    const height = getElementHeightByRef(this.props.parentRef)
    return height ? BOTTOM_OFFSET + height : DEFAULT_BOTTOM_VALUE
  }

  private renderEmbeddedUserDirectory() {
    const {
      shouldUserDirectoryShow,
      hideUserDirectory,
      hasUseDirectorySelectedItems,
      onChangeUserDirectorySelection,
      mentionFromDirectory,
    } = this.store

    return (
      shouldUserDirectoryShow && (
        <div className="embedded-user-directory col absolute full-height full-width">
          <div className="row pa12 bg-white">
            <div className="text blue-highlight">
              <ChevronLeftIcon size={iconSize} onClick={hideUserDirectory} />
            </div>
            <Button
              onClick={mentionFromDirectory}
              variant="default"
              className="no-grow bold"
              disabled={!hasUseDirectorySelectedItems}
            >
              {Localization.translator.mention}
            </Button>
          </div>
          <UsersDirectory
            isSelectionMode={true}
            onItemSelect={onChangeUserDirectorySelection}
            className="scrollable"
          />
        </div>
      )
    )
  }

  private handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    newValue: string,
    newPlainTextValue: string,
    mentions: IMention[],
  ) => {
    this.store.change(newValue, newPlainTextValue, mentions)
  }
}
